import {Fragment, useEffect, useState} from 'react';
import clsx from 'clsx';
import {Button} from '../../ui/buttons/button';
import {Trans} from '../../i18n/trans';
import {ForumIcon} from '../../icons/material/Forum';
import {Navbar} from '../../ui/navigation/navbar/navbar';
import {Link} from 'react-router-dom';
import {Footer} from '../../ui/footer/footer';
import {StaticPageTitle} from '../../seo/static-page-title';
import {PricingTable} from '@common/billing/pricing-table/pricing-table';
import {getBootstrapData} from '@common/core/bootstrap-data/use-backend-bootstrap-data';
import {useQuery} from '@tanstack/react-query';
import {apiClient} from '../../http/query-client';
import subscriber1 from './avatars/subscriber-1.webp';
import subscriber2 from './avatars/subscriber-2.webp';
import subscriber3 from './avatars/subscriber-3.webp';
import subscriber4 from './avatars/subscriber-4.webp';
import {TrialPricingNotice} from '@common/billing/trials/trial-banner';

export function PricingPage() {
  return (
    <Fragment>
      <StaticPageTitle>
        <Trans message="Pricing" />
      </StaticPageTitle>
      <Navbar
        color="bg"
        darkModeColor="transparent"
        border="border-b"
        menuPosition="pricing-table-page"
      />
      <div className="container mx-auto px-24 pb-96 md:pb-0">
        <TrialPricingNotice />
        <Hero />
        <section id="pricing-plans" className="scroll-mt-[80px]">
          <h2 className="text-center text-2xl font-bold md:text-3xl">
            <Trans message="اختر الخطة المناسبة لك" />
          </h2>
          <p className="mx-auto mt-8 max-w-[520px] text-center text-sm text-muted">
            <Trans message="يعمل خلال ثوانٍ، والمبلغ يُخصم من رصيدك أو فاتورتك أو بطاقتك. ألغِ متى شئت." />
          </p>
          <SocialProof />
          <PricingTable productLoader="pricingPage" layout="grid" />
        </section>
        <TrustSection />
        <FaqSection />
        <ContactSection />
      </div>
      <StickyMobileBar />
      <Footer className="container mx-auto flex-shrink-0 px-24" />
    </Fragment>
  );
}

function Hero() {
  return (
    <div className="py-40 text-center md:py-60">
      <h1 className="mx-auto max-w-[620px] text-4xl font-black leading-tight md:text-5xl">
        <Trans message="استمع بلا حدود." />
        <br />
        <span className="text-primary">
          <Trans message="اشترك عبر مشغلك." />
        </span>
      </h1>
      <p className="mx-auto mt-14 max-w-[520px] text-sm leading-relaxed text-muted md:text-base">
        <Trans message="آلاف الأغاني والأشعار السودانية، بدون إعلانات وبجودة عالية. الاشتراك يتم عبر مشغل شبكتك أو ببطاقتك، وألغِ في أي وقت." />
      </p>
    </div>
  );
}

// decorative listener faces for the social-proof row under the plans heading.
// square-cropped to 96px webp (~1.3KB each) since they render at 28px.
const AVATAR_PHOTOS = [subscriber1, subscriber2, subscriber3, subscriber4];

interface SocialProofResponse {
  subscriberCount: number;
}

// full page loads carry the count inside the page payload (no request);
// SPA navigations and restored tabs have no payload, so fall back to the
// dedicated API endpoint — otherwise the row silently disappears for them
function useSubscriberCount() {
  return useQuery<SocialProofResponse>({
    queryKey: ['billing/pricing/social-proof'],
    queryFn: () =>
      apiClient
        .get<SocialProofResponse>('billing/pricing/social-proof')
        .then(response => response.data),
    staleTime: Infinity,
    initialData: () => {
      // @ts-ignore loaders payload is untyped (same pattern as use-products)
      const count = getBootstrapData().loaders?.pricingPage?.subscriberCount;
      return typeof count === 'number' ? {subscriberCount: count} : undefined;
    },
  });
}

function SocialProof() {
  const {data} = useSubscriberCount();
  const subscriberCount = data?.subscriberCount;
  // hide the whole row rather than render a sentence with a missing number
  if (!subscriberCount) return null;
  return (
    <div className="mb-40 mt-16 flex items-center justify-center gap-10">
      <div className="flex">
        {AVATAR_PHOTOS.map((photo, index) => (
          <img
            key={index}
            src={photo}
            alt=""
            aria-hidden="true"
            loading="lazy"
            width={28}
            height={28}
            className="-ml-8 h-28 w-28 rounded-full border-2 border-bg object-cover first:ml-0"
          />
        ))}
      </div>
      <div className="text-sm font-semibold text-muted">
        <Trans
          message="انضم إلى أكثر من :count مشترك"
          values={{count: subscriberCount.toLocaleString('en-US')}}
        />
      </div>
    </div>
  );
}

const TRUST_ITEMS = [
  {
    title: 'الدفع عبر مشغلك',
    body: 'يُخصم مباشرة من رصيد جوالك أو فاتورتك الشهرية — بدون بطاقة ولا حساب بنكي.',
  },
  {
    title: 'أو ببطاقتك',
    body: 'فيزا وماستركارد عبر Stripe المشفّر. لا تمر بيانات بطاقتك بأنظمتنا ولا نحتفظ بها.',
  },
  {
    title: 'إلغاء فوري',
    body: 'بضغطة واحدة من الإعدادات، ويسري فوراً. بدون رسوم ولا مكالمات.',
  },
  {
    title: 'مكتبة سودانية',
    body: 'آلاف الأغاني والأشعار السودانية، بدون إعلانات ولا مقاطعة.',
  },
];

function TrustSection() {
  return (
    <section className="mt-60 md:mt-80">
      <h2 className="text-center text-2xl font-bold md:text-3xl">
        <Trans message="ادفع بالطريقة التي تناسبك" />
      </h2>
      <p className="mx-auto mt-8 max-w-[520px] text-center text-sm text-muted">
        <Trans message="عبر مشغل شبكتك أو ببطاقتك — الاشتراك يتم في ثوانٍ." />
      </p>
      <div className="mt-26 grid grid-cols-1 gap-12 sm:grid-cols-2 lg:grid-cols-4">
        {TRUST_ITEMS.map(item => (
          <div
            key={item.title}
            className="rounded-2xl border bg-alt p-18"
          >
            <div className="mb-6 text-sm font-bold">
              <Trans message={item.title} />
            </div>
            <div className="text-xs leading-relaxed text-muted">
              <Trans message={item.body} />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

const FAQ_ITEMS = [
  {
    question: 'كيف يتم الدفع عبر المشغل؟',
    answer:
      'الاشتراك في عازة يتم رسمياً عبر مشغل شبكتك: زين السعودية، موبايلي، STC، ليبارا، وRed Bull Mobile. تُخصم قيمة الاشتراك تلقائياً من رصيدك أو تُضاف إلى فاتورتك الشهرية حسب نوع خطك، وتصلك رسالة تأكيد من المشغل نفسه فور التفعيل — بدون إدخال أي بيانات بنكية.',
  },
  {
    question: 'هل يمكنني الإلغاء في أي وقت؟',
    answer: 'نعم. الإلغاء متاح فوراً من داخل المنصة.',
  },
  {
    question: 'هل الاشتراك آمن وموثوق؟',
    answer:
      'عازة معتمدة لدى مشغلي الاتصالات في السعودية والسودان. الاشتراك عبر المشغل يتم داخل أنظمته الرسمية بدون طلب رقمك السري أو بطاقتك، ويوثَّق برسالة منه. والدفع بالبطاقة يتم عبر Stripe المشفّر دون أن نحتفظ ببياناتها.',
  },
  {
    question: 'ما الفرق بين الخطة اليومية والشهرية؟',
    answer:
      'الخطة اليومية تُجدد كل ٢٤ ساعة وتُخصم من رصيد جوالك، وتناسب الاستخدام المتقطع. الخطة الشهرية تُدفع ببطاقتك وتوفر أكثر للمستخدمين الدائمين.',
  },
];

function FaqSection() {
  return (
    <section className="mx-auto mt-60 max-w-[760px] md:mt-80">
      <h2 className="text-center text-2xl font-bold md:text-3xl">
        <Trans message="أسئلة شائعة" />
      </h2>
      <p className="mt-8 text-center text-sm text-muted">
        <Trans message="كل ما تحتاج معرفته قبل الاشتراك." />
      </p>
      <div className="mt-20 border-t">
        {FAQ_ITEMS.map((item, index) => (
          <details
            key={item.question}
            className="group border-b"
            open={index === 0}
          >
            <summary className="flex cursor-pointer list-none items-center justify-between gap-12 py-18 text-sm font-semibold [&::-webkit-details-marker]:hidden md:text-base">
              <Trans message={item.question} />
              <span className="text-lg font-bold text-primary group-open:hidden">
                +
              </span>
              <span className="hidden text-lg font-bold text-primary group-open:block">
                −
              </span>
            </summary>
            <p className="pb-18 text-sm leading-loose text-muted">
              <Trans message={item.answer} />
            </p>
          </details>
        ))}
      </div>
    </section>
  );
}

function ContactSection() {
  return (
    <div className="my-20 p-24 text-center md:my-80">
      <ForumIcon size="xl" className="text-muted" />
      <div className="my-8 md:text-lg">
        <Trans message="Do you have any questions about PRO accounts?" />
      </div>
      <div className="mb-24 mt-20 text-sm md:mt-0 md:text-base">
        <Trans message="Our support team will be happy to assist you." />
      </div>
      <Button variant="flat" color="primary" elementType={Link} to="/contact">
        <Trans message="Contact us" />
      </Button>
    </div>
  );
}

function StickyMobileBar() {
  // the bar is a shortcut to the plan grid, so it is only useful once the grid
  // (with its own per-operator subscribe buttons) has scrolled off screen.
  // While the plans are in view we hide it; it slides in above the fold and
  // again once the visitor scrolls past the plans into the sections below.
  // deliberately plan-agnostic: it never assumes the visitor's operator/card,
  // it just brings them to the grid to choose for themselves.
  const [plansInView, setPlansInView] = useState(true);
  useEffect(() => {
    const el = document.getElementById('pricing-plans');
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      ([entry]) => setPlansInView(entry.isIntersecting),
      {threshold: 0},
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      aria-hidden={plansInView}
      className={clsx(
        'fixed bottom-0 left-0 right-0 z-30 flex items-center justify-between gap-14 border-t bg px-20 py-12 transition-transform duration-200 md:hidden',
        plansInView ? 'pointer-events-none translate-y-full' : 'translate-y-0',
      )}
    >
      <div className="min-w-0">
        <div className="text-sm font-bold">
          <Trans message="جاهز للاشتراك؟" />
        </div>
        <div className="truncate text-xs text-muted">
          <Trans message="اختر مشغلك أو بطاقتك وابدأ خلال ثوانٍ" />
        </div>
      </div>
      <Button
        variant="flat"
        color="primary"
        size="md"
        className="flex-shrink-0"
        onClick={() =>
          document
            .getElementById('pricing-plans')
            ?.scrollIntoView({behavior: 'smooth'})
        }
      >
        <Trans message="اشترك الآن" />
      </Button>
    </div>
  );
}
